import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Select, SelectItem, Textarea } from "@heroui/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { FileLogStatus } from "shared/types/file";
import PdfPreview from "shared/components/uploadFileModal/pdfPreview";
import SearchComponent, { SearchMode } from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import FileLabelSearchComponent from "shared/components/searchBar/components/fileLabelSearchComponent";

interface ScreenFileModalProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onClose: () => void;
    file: {
        id: string;
        name: string;
        url: string;
    };
    acquisitionId: number;
    buildings?: any[];
}

export const ScreenFileModal = ({
    isOpen,
    onOpenChange,
    onClose,
    file,
    acquisitionId,
    buildings = []
}: ScreenFileModalProps) => {
    const { session } = useSession();
    const [formState, setFormState] = useState({
        fileLabel: "",
        status: FileLogStatus.UNKNOWN,
        documentDate: "",
        expirationDate: "",
        statusDescription: "",
        description: "",
        buildingId: buildings?.length === 1 ? buildings[0]?.id : null,
        unitId: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLabelType, setSelectedLabelType] = useState<'building' | 'unit' | null>(null);

    // Memoize handlers
    const handleLabelSelect = useCallback((label: string) => {
        setFormState(prev => ({ ...prev, fileLabel: label }));
        
        fetchWithAuth(
            `${API_URL}/file/label-type/${encodeURIComponent(label)}`,
            { method: "GET" },
            session
        )
        .then(response => response.json())
        .then(data => {
            setSelectedLabelType(data.resourceType);
        })
        .catch(error => {
            console.error('Error fetching label type:', error);
        });
    }, [session]);

    const handleBuildingSelect = useCallback((building: any) => {
        setFormState(prev => ({
            ...prev,
            buildingId: building?.resourceId,
            unitId: null
        }));
    }, []);

    const handleUnitSelect = useCallback((unit: any) => {
        setFormState(prev => ({
            ...prev,
            unitId: unit?.resourceId
        }));
    }, []);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormState({
                fileLabel: "",
                status: FileLogStatus.UNKNOWN,
                documentDate: "",
                expirationDate: "",
                statusDescription: "",
                description: "",
                buildingId: buildings?.length === 1 ? buildings[0]?.id : null,
                unitId: null,
            });
            setSelectedLabelType(null);
        }
    }, [isOpen, buildings?.length, buildings?.[0]?.id]);

    const handleSubmit = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetchWithAuth(
                `${API_URL}/file/screen`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        sharePointFileId: file.id,
                        sharePointFileUrl: file.url,
                        ...formState,
                        acquisitionId,
                    })
                },
                session
            );

            if (!response.ok) {
                throw new Error('Failed to screen file');
            }

            toast.success('File successfully screened');
            onClose();
        } catch (error) {
            console.error('Error screening file:', error);
            toast.error('Failed to screen file');
        } finally {
            setIsLoading(false);
        }
    }, [file, formState, acquisitionId, session, onClose]);

    const isPdf = file.name.toLowerCase().endsWith('.pdf');

    // Memoize PDF props
    const pdfProps = useMemo(() => ({
        fileLog: {
            sharePointFileId: file.id,
            sharePointFileUrl: file.url
        },
        scale: 1.5,
        height: "750px"
    }), [file.id, file.url]);

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            size={isPdf ? "5xl" : "3xl"}
            scrollBehavior="inside"
            isDismissable={false}
        >
            <ModalContent>
                <ModalHeader>Screen Document: {file.name}</ModalHeader>
                <ModalBody className="gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-4">
                            <FileLabelSearchComponent
                                incomingSelectedLabel={formState.fileLabel}
                                onSelect={handleLabelSelect}
                                incomingResourceTypes={["building", "unit"]}
                                incomingDocumentTypes={["document", "picture"]}
                                labelType="full"
                            />

                            {buildings?.length !== 1 && (
                                <SearchComponent
                                    editMode={true}
                                    label="Selecteer gebouw"
                                    onSelect={handleBuildingSelect}
                                    resourceFilter={[ResourceType.BUILDING]}
                                    initialValue={formState.buildingId || ""}
                                    mode={SearchMode.SEARCH_AND_PRESENT}
                                />
                            )}

                            {selectedLabelType === 'unit' && (
                                <SearchComponent
                                    editMode={true}
                                    label="Selecteer unit"
                                    onSelect={handleUnitSelect}
                                    resourceFilter={[ResourceType.UNIT]}
                                    initialValue={formState.unitId || ""}
                                    mode={SearchMode.SEARCH_AND_PRESENT}
                                />
                            )}

                            <Select
                                label="Status"
                                value={formState.status}
                                onChange={(e) => setFormState(prev => ({ ...prev, status: e.target.value as FileLogStatus }))}
                                isRequired
                            >
                                {Object.values(FileLogStatus).map((status) => (
                                    <SelectItem key={status}>
                                        {status}
                                    </SelectItem>
                                ))}
                            </Select>

                            <Textarea
                                label="Waarom deze status?"
                                value={formState.statusDescription}
                                onChange={(e) => setFormState(prev => ({ ...prev, statusDescription: e.target.value }))}
                            />

                            <Input
                                type="date"
                                label="Document Date"
                                value={formState.documentDate}
                                onChange={(e) => setFormState(prev => ({ ...prev, documentDate: e.target.value }))}
                            />

                            <Input
                                type="date"
                                label="Expiration Date"
                                value={formState.expirationDate}
                                onChange={(e) => setFormState(prev => ({ ...prev, expirationDate: e.target.value }))}
                            />

                            <Textarea
                                label="Beschrijving of opmerkingen bij het document"
                                value={formState.description}
                                onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        {isPdf && (
                            <div className="h-[800px] overflow-y-auto">
                                <PdfPreview {...pdfProps} />
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            color="primary"
                            onClick={handleSubmit}
                            isLoading={isLoading}
                        >
                            Screen Document
                        </Button>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}; 